import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useArtistImages, type ArtistImage } from "@/hooks/useArtistImage";

/**
 * Artist promo photo(s) for the schedule's artist-detail dialog.
 *
 * - Renders nothing when the admin toggle is off, while loading has no result,
 *   or when there is no confident Spotify match.
 * - B2B / vs sets show one photo per artist (each labelled).
 * - Clicking a photo opens it enlarged in a lightbox.
 */
export function ArtistPhoto({ artistName }: { artistName: string | null | undefined }) {
  const { artists, isLoading, featureEnabled } = useArtistImages(artistName);
  const [zoomed, setZoomed] = useState<ArtistImage | null>(null);

  if (!featureEnabled) return null;

  if (isLoading) {
    return (
      <div
        className="h-40 w-40 animate-pulse rounded-lg bg-muted"
        aria-label="Loading artist photo"
      />
    );
  }

  if (artists.length === 0) return null;

  const showNames = artists.length > 1;

  return (
    <>
      <div className="flex flex-wrap gap-3">
        {artists.map((artist, index) => (
          <button
            key={`${artist.name}-${artist.url}-${index}`}
            type="button"
            onClick={() => setZoomed(artist)}
            className="group flex flex-col items-center gap-1 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            title="Click to enlarge"
          >
            <img
              src={artist.url}
              alt={artist.name}
              // Eager: the dialog only renders once it is open, so this photo
              // is the thing the producer just asked for — never defer it.
              loading="eager"
              className="h-40 w-40 rounded-lg object-cover transition-transform group-hover:scale-[1.02]"
            />
            {showNames && (
              <span className="max-w-[10rem] truncate text-xs font-medium">{artist.name}</span>
            )}
          </button>
        ))}
      </div>

      <Dialog open={!!zoomed} onOpenChange={(open) => !open && setZoomed(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{zoomed?.name}</DialogTitle>
          </DialogHeader>
          {zoomed && (
            <div className="space-y-2">
              <img
                src={zoomed.url}
                alt={zoomed.name}
                className="max-h-[70vh] w-full rounded-lg object-contain"
              />
              {zoomed.spotifyUrl && (
                <a
                  href={zoomed.spotifyUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-muted-foreground hover:underline"
                >
                  Open on Spotify
                </a>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
